import { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import {
  DEFAULT_UI_VISIBILITY,
  TOGGLEABLE_NAV,
  useBiUiVisibility,
  type BiUiVisibility,
} from '../state/BiUiVisibilityContext'

/** Preferências de exibição (Ctrl+H) — config global do sistema; só o admin principal edita. */
export function UiVisibilityDialog() {
  const { prefs, setPrefs, dialogOpen, closeDialog, canEdit, saveError } = useBiUiVisibility()
  const [draft, setDraft] = useState<BiUiVisibility>(prefs)
  const [saving, setSaving] = useState(false)
  const [localError, setLocalError] = useState<string | null>(null)

  useEffect(() => {
    if (dialogOpen) {
      setDraft({
        ...prefs,
        nav: { ...prefs.nav },
      })
      setLocalError(null)
    }
  }, [dialogOpen, prefs])

  const save = async () => {
    setSaving(true)
    setLocalError(null)
    try {
      await setPrefs({
        ...draft,
        nav: { ...draft.nav },
      })
      closeDialog()
    } catch (e) {
      setLocalError(e instanceof Error ? e.message : 'Falha ao guardar')
    } finally {
      setSaving(false)
    }
  }

  const resetDefaults = () => {
    setDraft({
      ...DEFAULT_UI_VISIBILITY,
      nav: { ...DEFAULT_UI_VISIBILITY.nav },
    })
  }

  return (
    <Dialog open={dialogOpen && canEdit} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>Elementos exibidos no BI</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Esta configuração vale para <strong>todo o sistema</strong> (todos os logins). Nada é
          removido — só some da interface. Atalho: <strong>Ctrl+H</strong>.
        </Typography>

        {(localError || saveError) && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {localError || saveError}
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.04}>
          ÁREA SUPERIOR
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.75, mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.fonteDeDados}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, fonteDeDados: e.target.checked }))
                }
              />
            }
            label="FONTE DE DADOS"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={draft.proveniencia}
                onChange={(e) =>
                  setDraft((prev) => ({ ...prev, proveniencia: e.target.checked }))
                }
              />
            }
            label="PROVENIÊNCIA"
          />
        </Stack>

        <Typography variant="caption" color="text.secondary" fontWeight={700} letterSpacing={0.04}>
          MENU LATERAL
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.75 }}>
          {TOGGLEABLE_NAV.map((item) => (
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  checked={draft.nav[item.id]}
                  onChange={(e) =>
                    setDraft((prev) => ({
                      ...prev,
                      nav: { ...prev.nav, [item.id]: e.target.checked },
                    }))
                  }
                />
              }
              label={item.label}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={resetDefaults} sx={{ mr: 'auto' }} disabled={saving}>
          Restaurar padrão
        </Button>
        <Button onClick={closeDialog} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  )
}
